import { LRUCache } from 'lru-cache';

interface iNaturalistResponse {
  results: Array<{
    id: number;
    iconic_taxon_name: string;
    preferred_common_name?: string;
    name: string;
    default_photo: {
      medium_url: string;
      url: string;
      attribution: string;
      license_code: string;
    } | null;
  }>;
  total_results?: number;
}

interface SpeciesImageResult {
  url?: string;
  attribution?: string;
  license?: string;
  error?: boolean;
  message?: string;
  fallbackImage?: string;
}

interface SpeciesDetailsResult {
  habitats?: string[];
  description?: string;
  conservationStatus?: string;
  wikipediaUrl?: string;
  error?: string;
}

interface iNaturalistTaxonResponse {
  results: Array<{
    id: number;
    name: string;
    preferred_common_name?: string;
    wikipedia_url?: string;
    iconic_taxon_name?: string;
    taxonomy?: {
      order?: string;
      family?: string;
      genus?: string;
    };
    conservation_status?: {
      status?: string;
      iucn?: string;
      description?: string;
    };
    wikipedia_summary?: string;
    atlas_id?: string;
    taxon_changes?: any[];
    taxon_schemes?: any[];
    taxon_framework_relationship?: any;
    flag_counts?: any;
    current_synonymous_taxon_ids?: number[];
    taxon_photos?: Array<{
      photo?: {
        id?: number;
        url?: string;
      };
    }>;
  }>;
}

const validatePhotoUrl = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error('Error validating photo URL:', error);
    return false;
  }
};

// Test function to check API response
const testSpeciesSearch = async (commonName: string) => {
  try {
    const searchUrl = `https://api.inaturalist.org/v1/taxa?q=${encodeURIComponent(commonName)}&per_page=1&locale=en`;
    console.log('Test search URL:', searchUrl);
    
    const response = await fetch(searchUrl, {
      headers: {
        'Accept': 'application/json'
      }
    });
    const rawText = await response.text();
    console.log('Raw API response:', rawText);
    
    try {
      const data = JSON.parse(rawText);
      console.log('Parsed response:', data);
      return data;
    } catch (parseError) {
      console.error('Error parsing response:', parseError);
      return null;
    }
  } catch (error) {
    console.error('Error in test search:', error);
    return null;
  }
};

// Run test for a sample species
testSpeciesSearch('African Elephant');

// Utility function to check internet connection
const checkConnection = async (): Promise<boolean> => {
  try {
    const response = await fetch('https://api.inaturalist.org/v1/ping', { 
      method: 'HEAD'
    });
    return response.ok;
  } catch (error) {
    console.error('Connection check failed:', error);
    return false;
  }
};

// Retry mechanism for API calls
const retryFetch = async (
  url: string, 
  options: RequestInit = {}, 
  maxRetries: number = 3,
  delay: number = 1000
): Promise<Response> => {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      
      if (response.ok) {
        return response;
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      lastError = error as Error;
      console.warn(`Attempt ${attempt}/${maxRetries} failed:`, error);
      
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }
  
  throw new Error(`Failed after ${maxRetries} attempts. Last error: ${lastError?.message}`);
};

// Cache configuration
const cache = new LRUCache<string, any>({
  max: 500, // Maximum number of items to store
  ttl: 1000 * 60 * 60 * 24, // 24 hour TTL
  allowStale: false,
  updateAgeOnGet: true,
  updateAgeOnHas: true,
  noDisposeOnSet: true
});

// Rate limiting configuration
const RATE_LIMIT = {
  windowMs: 60 * 1000, // 1 minute
  maxRequests: 30, // Maximum requests per minute
};

let lastRequestTime = 0;
let requestsInWindow = 0;

// API configuration
const INATURALIST_API_BASE = 'https://api.inaturalist.org/v1';

// Error types
class INaturalistError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = 'INaturalistError';
  }
}

class RateLimitError extends INaturalistError {
  constructor() {
    super('Rate limit exceeded. Please try again later.', 429);
    this.name = 'RateLimitError';
  }
}

// Rate limiting function
const checkRateLimit = () => {
  const now = Date.now();
  if (now - lastRequestTime > RATE_LIMIT.windowMs) {
    // Reset if window has passed
    lastRequestTime = now;
    requestsInWindow = 0;
  }

  if (requestsInWindow >= RATE_LIMIT.maxRequests) {
    throw new RateLimitError();
  }

  requestsInWindow++;
  lastRequestTime = now;
};

// Helper function to make API requests
const makeRequest = async (endpoint: string): Promise<any> => {
  checkRateLimit();

  const cachedResponse = cache.get(endpoint);
  if (cachedResponse) {
    return cachedResponse;
  }

  const response = await fetch(`${INATURALIST_API_BASE}${endpoint}`);
  
  if (!response.ok) {
    throw new INaturalistError(`API request failed: ${response.statusText}`, response.status);
  }

  const data = await response.json();
  cache.set(endpoint, data);
  return data;
};

export const getSpeciesImage = async (
  commonName: string,
  scientificName?: string
): Promise<SpeciesImageResult | null> => {
  try {
    // Check connection first
    const isConnected = await checkConnection();
    if (!isConnected) {
      console.error('No connection to iNaturalist API');
      throw new Error('Unable to connect to iNaturalist API. Please check your internet connection or VPN.');
    }

    // Prioritize scientific name if available
    const searchTerm = scientificName || commonName;
    console.log('Fetching iNaturalist image using:', { searchTerm, type: scientificName ? 'scientific' : 'common' });
    
    const searchUrl = `https://api.inaturalist.org/v1/taxa?q=${encodeURIComponent(searchTerm)}&per_page=5&locale=en&preferred_place_id=1`;
    console.log('Searching with URL:', searchUrl);
    
    const response = await retryFetch(searchUrl, {
      headers: {
        'Accept': 'application/json'
      }
    });

    const data: iNaturalistResponse = await response.json();
    console.log('Search results:', {
      totalResults: data.total_results,
      resultsFound: data.results?.length || 0
    });

    // Find the first result with a photo that matches our search term
    const matchingResult = data.results.find(result => {
      // For scientific name search, check exact match of the name field
      if (scientificName) {
        return result.name.toLowerCase() === scientificName.toLowerCase() && result.default_photo;
      }
      // For common name search, check includes in either name or preferred_common_name
      return (
        (result.preferred_common_name?.toLowerCase().includes(commonName.toLowerCase()) ||
        result.name.toLowerCase().includes(commonName.toLowerCase())) &&
        result.default_photo
      );
    });

    if (matchingResult?.default_photo) {
      console.log('Found matching result:', {
        name: matchingResult.name,
        commonName: matchingResult.preferred_common_name,
        searchType: scientificName ? 'scientific' : 'common'
      });

      return {
        url: matchingResult.default_photo.medium_url || matchingResult.default_photo.url,
        attribution: matchingResult.default_photo.attribution,
        license: matchingResult.default_photo.license_code
      };
    }

    // If no exact match found with scientific name, try common name as fallback
    if (scientificName && commonName) {
      console.log('No match with scientific name, trying common name:', commonName);
      return getSpeciesImage(commonName);
    }

    console.log('No matching images found for species:', searchTerm);
    return null;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('Error fetching species image:', errorMessage);
    
    // Return a more informative error response
    return {
      error: true,
      message: errorMessage,
      fallbackImage: '/species-fallback.png'
    } as any;
  }
};

// Habitat types mapped to taxonomic groups
const taxonomicHabitats: Record<string, string[]> = {
  'Mammalia': ['Forests', 'Grasslands', 'Mountains', 'Wetlands', 'Desert'],
  'Aves': ['Forests', 'Wetlands', 'Coastal areas', 'Grasslands', 'Urban areas'],
  'Reptilia': ['Forests', 'Deserts', 'Grasslands', 'Freshwater', 'Rocky areas'],
  'Amphibia': ['Freshwater', 'Temperate forests', 'Tropical forests', 'Wetlands'],
  'Actinopterygii': ['Coral reefs', 'Open ocean', 'Freshwater', 'Coastal waters'],
  'Chondrichthyes': ['Open ocean', 'Coastal waters', 'Deep sea', 'Coral reefs'],
  'default': ['Terrestrial', 'Aquatic']
};

// Get species details from iNaturalist API
export const getSpeciesDetails = async (
  commonName: string,
  scientificName?: string
): Promise<SpeciesDetailsResult> => {
  try {
    // Prioritize scientific name for search if available
    const searchTerm = scientificName || commonName;
    console.log('Fetching species details for:', { searchTerm });
    
    // First get basic taxonomic information with Wikipedia data
    const taxonUrl = `${INATURALIST_API_BASE}/taxa?q=${encodeURIComponent(searchTerm)}&per_page=1&all_names=true&locale=en&preferred_place_id=1`;
    console.log('Fetching from URL:', taxonUrl);
    const taxonResponse = await fetch(taxonUrl);
    
    if (!taxonResponse.ok) {
      throw new Error(`iNaturalist API responded with status: ${taxonResponse.status}`);
    }
    
    const taxonData = await taxonResponse.json();
    console.log('Initial search response:', {
      results: taxonData.results?.length,
      firstResult: taxonData.results?.[0]
    });
    
    const speciesInfo = taxonData.results[0];
    
    if (!speciesInfo) {
      console.log('No species information found');
      return {
        habitats: ['Unknown']
      };
    }

    // Get detailed information including Wikipedia summary
    const detailsUrl = `${INATURALIST_API_BASE}/taxa/${speciesInfo.id}?all_names=true&locale=en&preferred_place_id=1`;
    console.log('Fetching details from URL:', detailsUrl);
    const detailsResponse = await fetch(detailsUrl);
    
    if (!detailsResponse.ok) {
      throw new Error(`iNaturalist API details request failed: ${detailsResponse.status}`);
    }
    
    const detailsData = await detailsResponse.json();
    console.log('Detailed info response:', {
      results: detailsData.results?.length,
      firstResult: {
        id: detailsData.results?.[0]?.id,
        name: detailsData.results?.[0]?.name,
        wikipedia_summary: detailsData.results?.[0]?.wikipedia_summary?.substring(0, 100),
        has_summary: !!detailsData.results?.[0]?.wikipedia_summary
      }
    });
    
    const detailedInfo = detailsData.results[0];
    
    // Determine taxonomic group for habitat estimation
    const taxonClass = speciesInfo.iconic_taxon_name || 'default';
    
    // Determine likely habitats based on taxonomic group
    const habitats = taxonomicHabitats[taxonClass] || taxonomicHabitats.default;
    
    const result = {
      habitats,
      description: detailedInfo?.wikipedia_summary,
      conservationStatus: speciesInfo.conservation_status?.status || 'Unknown',
      wikipediaUrl: detailedInfo?.wikipedia_url || speciesInfo.wikipedia_url
    };
    
    console.log('Returning species details:', {
      hasDescription: !!result.description,
      descriptionLength: result.description?.length,
      firstLine: result.description?.split('\n')[0]?.substring(0, 100)
    });
    
    return result;
  } catch (error) {
    console.error('Error fetching species details:', error);
    return {
      habitats: ['Unknown']
    };
  }
};